import type {CodeEdit} from './verification-code-input.types';
import {VERIFICATION_CODE_LENGTH} from './verification-code-input.types';

const NON_DIGITS = /\D/g;
/** A box cleared in the middle holds a space, so every later digit keeps its position. */
const EMPTY_BOX = ' ';

export const toCodeDigits = (text: string): string => {
  return text.replace(NON_DIGITS, '');
};

const placeAt = (code: string, index: number, characters: string): string => {
  const padded = code.padEnd(VERIFICATION_CODE_LENGTH, EMPTY_BOX);
  return `${padded.slice(0, index)}${characters}${padded.slice(index + characters.length)}`.slice(0, VERIFICATION_CODE_LENGTH).trimEnd();
};

/**
 * What the user typed into a box, without the digit the box already held: typing into a filled box reports both
 * (`"39"` or `"93"` depending on the caret), and only the new one is the edit.
 */
const getTypedDigits = (boxValue: string, previousDigit: string): string => {
  const digits = toCodeDigits(boxValue);
  if (previousDigit === '' || digits.length !== 2) {
    return digits;
  }
  const withoutPrevious = digits.replace(previousDigit, '');
  return withoutPrevious === '' ? previousDigit : withoutPrevious;
};

/**
 * Typing replaces the digit in this box and leaves the rest of the code alone. Several digits at once (fast typing,
 * an autofilled one-time code) fill this box and the ones after it.
 */
export const getCodeAfterTyping = (code: string, index: number, boxValue: string): CodeEdit => {
  const previousDigit = (code[index] ?? '').trim();
  const typed = getTypedDigits(boxValue, previousDigit);
  if (typed === '') {
    return {code, focusIndex: index};
  }
  return {
    code: placeAt(code, index, typed.slice(0, VERIFICATION_CODE_LENGTH - index)),
    focusIndex: Math.min(index + typed.length, VERIFICATION_CODE_LENGTH - 1),
  };
};

/** Backspace clears this box in place, or, when it is already empty, the box before it, and moves there. */
export const getCodeAfterBackspace = (code: string, index: number): CodeEdit => {
  if ((code[index] ?? '').trim() !== '') {
    return {code: placeAt(code, index, EMPTY_BOX), focusIndex: index};
  }
  const previousIndex = Math.max(index - 1, 0);
  return {code: placeAt(code, previousIndex, EMPTY_BOX), focusIndex: previousIndex};
};
